// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// What a job actually exposes you to, against what actually happens to people
// doing it. 318 occupations x 57 hazard variables.
//
// The source ships its own per-occupation employment, claim counts and claim
// rates alongside the exposure scores, so the scatter here joins exposure to
// outcome at the SAME four-digit occupation level — no rollup, no averaging
// across a coarser code, no borrowed weights.

import { card, chartBox, legend, note, rowActivationAttrs, viewHead } from './ui';
import { linear } from './layout';
import { gloss } from '../glossary';
import { esc, money, num, period, rate, tipAttr, weeks } from '../format';
import { HAZARD_STOPS, PHYSICAL, PSYCH, hazardColor } from '../colors';
import { openDrawer } from '../drawer';
import type { Bundle, Hazards, Val } from '../types';

export const title = 'Hazards';

let family = 'All';
let sortHazard = -1;
let query = '';
let axisMode: 'physical' | 'conflict' = 'physical';

function families(h: Hazards): string[] {
  // The publisher's own grouping, in the order it appears in the source.
  const seen: string[] = [];
  for (const x of h.hazards) if (!seen.includes(x.family)) seen.push(x.family);
  return ['All', ...seen];
}

/** Mean of a set of columns for one occupation, ignoring unscored ones. */
function meanOf(scores: Val[], cols: number[]): number | null {
  const vals = cols.map((i) => scores[i]).filter((v): v is number => v !== null && Number.isFinite(v));
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
}

export function render(b: Bundle): string {
  const h = b.hazards;
  const cols = h.hazards.map((x, i) => ({ ...x, i })).filter((x) => family === 'All' || x.family === family);
  const q = query.trim().toLowerCase();

  const rows = h.occupations.filter((o) => !q || o.title.toLowerCase().includes(q));
  const sorted = sortHazard >= 0 ? [...rows].sort((a, c) => (c.s[sortHazard] ?? -1) - (a.s[sortHazard] ?? -1)) : rows;
  const shown = sorted.slice(0, 80);

  // Both axis definitions use the publisher's own families, unaltered.
  const PHYSICAL_FAMILIES = ['Body Positioning', 'Environmental Conditions', 'Job Hazards'];
  const physicalCols = h.hazards.map((x, i) => ({ x, i })).filter(({ x }) => PHYSICAL_FAMILIES.includes(x.family)).map(({ i }) => i);
  const conflictCols = h.hazards.map((x, i) => ({ x, i })).filter(({ x }) => x.family === 'Conflictual Contact').map(({ i }) => i);
  const useCols = axisMode === 'physical' ? physicalCols : conflictCols;

  const points = h.occupations
    .map((o) => {
      const x = meanOf(o.s, useCols);
      const y = o.frequency;
      if (x === null || y === null) return null;
      return { o, x, y };
    })
    .filter(Boolean) as { o: Hazards['occupations'][number]; x: number; y: number }[];

  const withEmployment = h.occupations.filter((o) => o.employment !== null);
  const totalEmployment = withEmployment.reduce((a, o) => a + (o.employment ?? 0), 0);

  // Correlation, computed and stated rather than implied by the eye.
  const r = pearson(points.map((p) => p.x), points.map((p) => p.y));

  const riskiest = [...h.occupations]
    .filter((o) => o.frequency !== null && (o.employment ?? 0) >= 5)
    .sort((a, c) => (c.frequency ?? 0) - (a.frequency ?? 0))
    .slice(0, 12);

  return `
    ${viewHead(
      'Hazards',
      'What does my job actually expose me to, and does it show up in the injuries?',
      `Safe Work Australia scores ${h.occupations.length} occupations against ${h.hazards.length} hazards — climbing ladders, cramped spaces, contaminants, dealing with physically aggressive people — on a 0–100 scale, and publishes each occupation's real claim rate alongside them. That means exposure and outcome can be compared at the same level of detail, which is unusual.`,
    )}

    <div class="ctl-row">
      <label class="sel-lab" for="haz-group">Hazard family</label>
      <select id="haz-group">${families(h).map((f) => `<option value="${esc(f)}"${f === family ? ' selected' : ''}>${esc(f)}</option>`).join('')}</select>
      <label class="sel-lab" for="haz-search">Occupation</label>
      <input type="search" id="haz-search" placeholder="Try “nurse” or “roof”" value="${esc(query)}" />
      <span class="ctl-note">${sorted.length} occupations${shown.length < sorted.length ? `, showing the first ${shown.length}` : ''} · click a hazard column to rank by it · click a row for its detail</span>
    </div>

    ${card(
      'Exposure matrix',
      `Each cell is how much a typical worker in that occupation is exposed to that hazard, 0 to 100. Darker is more exposure. ${sortHazard >= 0 ? `Ranked by <strong>${esc(h.hazards[sortHazard].name)}</strong>.` : 'Click any hazard column heading to rank every occupation by it.'}`,
      `<div class="mx-wrap haz-wrap"><table class="mx haz-mx">
        <thead><tr><th class="mx-corner">Occupation</th>${cols
          .map((c) => `<th scope="col" class="haz-col${c.i === sortHazard ? ' active' : ''}"><button type="button" class="haz-sort" data-haz="${c.i}" title="${esc(h.descriptions[c.name] ?? c.name)}"><span>${esc(c.name)}</span></button></th>`)
          .join('')}</tr></thead>
        <tbody>${shown
          .map(
            (o) => `<tr class="clickable" data-occ="${esc(o.code)}"${rowActivationAttrs(`${o.title}. Open its hazard profile.`)}><th scope="row">${esc(o.title)}<small>${esc(o.code)}${o.employment !== null ? ` · ${num(Math.round(o.employment * 1000))} employed` : ''}</small></th>${cols
              .map((c) => {
                const v = o.s[c.i];
                return `<td class="mx-cell" style="background:${hazardColor(v)}" data-tip="${tipAttr(`${o.title}\n${c.name}: ${v === null ? 'not scored' : `${v.toFixed(0)} / 100`}\n\n${h.descriptions[c.name] ?? ''}`)}"><span>${v === null ? '—' : v.toFixed(0)}</span></td>`;
              })
              .join('')}</tr>`,
          )
          .join('')}</tbody></table></div>` +
        `<div class="legend"><span class="legend-item">Never or rarely<span class="ramp">${HAZARD_STOPS.map((c) => `<i style="background:${c}"></i>`).join('')}</span>Constantly</span></div>`,
    )}

    ${card(
      'Does exposure show up in the injury rate?',
      `Each dot is one occupation. The horizontal axis is its average exposure across the ${axisMode === 'physical' ? 'Body Positioning, Environmental Conditions and Job Hazards' : 'Conflictual Contact'} families; the vertical axis is the serious claims that occupation actually generates per million hours worked. Both come from the same file at the same level of detail. The correlation is <strong>r = ${r === null ? '—' : r.toFixed(2)}</strong> — ${describeR(r)}.`,
      `<div class="ctl-row">
        <div class="seg" role="group" aria-label="hazard axis">
          <button type="button" data-seg="haxis" data-value="physical" class="${axisMode === 'physical' ? 'active' : ''}" aria-pressed="${axisMode === 'physical'}">Physical hazards</button>
          <button type="button" data-seg="haxis" data-value="conflict" class="${axisMode === 'conflict' ? 'active' : ''}" aria-pressed="${axisMode === 'conflict'}">Conflict and aggression</button>
        </div>
      </div>` +
        chartBox(scatterSvg(points, axisMode), 'wide') +
        legend([{ color: axisMode === 'physical' ? PHYSICAL : PSYCH, label: 'One occupation' }]),
    )}

    ${card(
      'The occupations with the highest claim rates',
      `Serious claims per million hours worked, for occupations employing at least 5,000 people. These are the source's own figures, five-year averages, published per occupation.`,
      `<div class="tbl-wrap"><table class="data" style="min-width:640px">
        <thead><tr><th scope="col">Occupation</th><th scope="col" class="num">Per M hours</th><th scope="col" class="num">Per 1,000</th><th scope="col" class="num">Claims</th><th scope="col" class="num">Employed</th></tr></thead>
        <tbody>${riskiest
          .map(
            (o) => `<tr class="clickable" data-occ="${esc(o.code)}"${rowActivationAttrs(`${o.title}. Open its hazard profile.`)} data-tip="${tipAttr(`${o.title}\nClick for its full hazard profile`)}">
              <td class="wide">${esc(o.title)}</td>
              <td class="num">${rate(o.frequency, 1)}</td>
              <td class="num">${rate(o.incidence, 1)}</td>
              <td class="num">${num(o.claims)}</td>
              <td class="num">${num(o.employment === null ? null : Math.round(o.employment * 1000))}</td>
            </tr>`,
          )
          .join('')}</tbody></table></div>`,
    )}

    ${note(
      `<strong>What this dataset is, and is not.</strong> Safe Work Australia publishes it as a <em>beta</em> and has not updated it since December 2023, while everything else on this site refreshes annually — so its claim figures are five-year averages to 2021–22, not the current year. The exposure scores are derived from the American O*NET survey and mapped onto Australian occupation codes rather than measured here, which is why Crop Farmers, Livestock Farmers and Mixed Crop and Livestock Farmers carry byte-identical scores. And exposure is not risk: the occupations with the highest exposure are often the ones with the most controls, which is part of why the correlation above is as loose as it is. The ${h.occupations.length} occupations shown cover ${num(Math.round(totalEmployment))},000 employed people.`,
      'warn',
    )}

    ${note(
      `Variable names, families and scores are all exactly as Safe Work Australia publishes them — nothing here is regrouped or renamed. ${gloss('Exposure scores', 'hazard exposure score')} are explained in the glossary.`,
    )}
  `;
}

function describeR(r: number | null): string {
  if (r === null) return 'not enough data to compute one';
  const a = Math.abs(r);
  const dir = r >= 0 ? 'positive' : 'negative';
  if (a < 0.2) return `essentially no relationship`;
  if (a < 0.4) return `a weak ${dir} relationship`;
  if (a < 0.6) return `a moderate ${dir} relationship`;
  return `a strong ${dir} relationship`;
}

function pearson(xs: number[], ys: number[]): number | null {
  const n = xs.length;
  if (n < 3) return null;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < n; i++) {
    const a = xs[i] - mx;
    const c = ys[i] - my;
    num += a * c;
    dx += a * a;
    dy += c * c;
  }
  const d = Math.sqrt(dx * dy);
  return d === 0 ? null : num / d;
}

function scatterSvg(points: { o: Hazards['occupations'][number]; x: number; y: number }[], mode: 'physical' | 'conflict'): string {
  const W = 820;
  const H = 400;
  const box = { x: 62, y: 16, w: W - 90, h: H - 62 };
  if (points.length < 3) return '<p class="empty">Not enough occupations carry both an exposure score and a claim rate.</p>';
  const maxY = Math.max(...points.map((p) => p.y)) * 1.12;
  const x = linear([0, 100], [box.x, box.x + box.w]);
  const y = linear([0, maxY], [box.y + box.h, box.y]);
  const colour = mode === 'physical' ? PHYSICAL : PSYCH;

  // role="group", not "img": every dot below is a tab stop, and role="img"
  // makes the whole subtree presentational — hiding all 312 of them from a
  // screen reader while leaving them in the tab order.
  let out = `<svg viewBox="0 0 ${W} ${H}" class="chart-svg" role="group" aria-label="Hazard exposure against serious claim frequency, ${points.length} occupations">`;
  for (let t = 0; t <= 5; t++) {
    const gx = box.x + (box.w / 5) * t;
    const gy = box.y + (box.h / 5) * t;
    out += `<line class="ax-grid" x1="${gx.toFixed(1)}" y1="${box.y}" x2="${gx.toFixed(1)}" y2="${(box.y + box.h).toFixed(1)}"/>`;
    out += `<line class="ax-grid" x1="${box.x}" y1="${gy.toFixed(1)}" x2="${(box.x + box.w).toFixed(1)}" y2="${gy.toFixed(1)}"/>`;
    out += `<text class="ax-txt" x="${gx.toFixed(1)}" y="${(box.y + box.h + 16).toFixed(1)}" text-anchor="middle">${(20 * t).toFixed(0)}</text>`;
    out += `<text class="ax-txt" x="${(box.x - 7).toFixed(1)}" y="${(gy + 3).toFixed(1)}" text-anchor="end">${(maxY - (maxY / 5) * t).toFixed(0)}</text>`;
  }
  out += `<text class="ax-title" x="${(box.x + box.w / 2).toFixed(1)}" y="${(H - 6).toFixed(1)}" text-anchor="middle">Average ${mode === 'physical' ? 'physical' : 'conflict'} hazard exposure, 0–100 →</text>`;
  out += `<text class="ax-title" transform="translate(13 ${(box.y + box.h / 2).toFixed(1)}) rotate(-90)" text-anchor="middle">Serious claims per million hours →</text>`;

  for (const p of points) {
    const cx = x(p.x);
    const cy = y(p.y);
    const r = p.o.employment ? Math.max(3, Math.min(13, 2.4 + Math.sqrt(p.o.employment) * 0.55)) : 3.4;
    out += `<circle class="dot" cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r.toFixed(1)}" fill="${colour}" data-tip="${tipAttr(`${p.o.title}\nExposure: ${p.x.toFixed(0)} / 100\nSerious claims: ${rate(p.y, 1)} per million hours\n${num(p.o.claims)} claims · ${num(p.o.employment, 1)}k employed`)}" data-occ="${esc(p.o.code)}" tabindex="0" role="button" aria-label="${esc(`${p.o.title}: exposure ${p.x.toFixed(0)}, ${rate(p.y, 1)} claims per million hours`)}"/>`;
  }
  return `${out}</svg>`;
}

export function mount(root: HTMLElement, b: Bundle, rerender: () => void): void {
  root.querySelector<HTMLSelectElement>('#haz-group')?.addEventListener('change', (e) => {
    family = (e.target as HTMLSelectElement).value;
    sortHazard = -1;
    rerender();
  });

  root.querySelectorAll<HTMLButtonElement>('[data-seg="haxis"]').forEach((btn) => {
    btn.addEventListener('click', () => { axisMode = btn.dataset.value === 'conflict' ? 'conflict' : 'physical'; rerender(); });
  });

  const search = root.querySelector<HTMLInputElement>('#haz-search');
  if (search) {
    let timer: number | undefined;
    search.addEventListener('input', () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        query = search.value;
        rerender();
        const again = document.querySelector<HTMLInputElement>('#haz-search');
        if (again) { again.focus(); again.setSelectionRange(again.value.length, again.value.length); }
      }, 300);
    });
  }

  root.querySelectorAll<HTMLButtonElement>('.haz-sort').forEach((btn) => {
    btn.addEventListener('click', () => {
      const i = Number(btn.dataset.haz);
      sortHazard = sortHazard === i ? -1 : i;
      rerender();
    });
  });

  const open = (code: string | undefined) => {
    const occ = b.hazards.occupations.find((o) => o.code === code);
    if (occ) openOccupationDrawer(b, occ);
  };
  root.querySelectorAll<HTMLElement>('[data-occ]').forEach((el) => {
    el.addEventListener('click', () => open(el.dataset.occ));
    el.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') { e.preventDefault(); open(el.dataset.occ); }
    });
  });
}

function openOccupationDrawer(b: Bundle, occ: Hazards['occupations'][number]): void {
  const h = b.hazards;
  const P = b.claims.periods;
  const scored = h.hazards
    .map((x, i) => ({ ...x, v: occ.s[i] }))
    .filter((x): x is { name: string; family: string; v: number } => x.v !== null)
    .sort((a, c) => c.v - a.v);

  const sub = b.claims.occupation.find((r) => r.code === occ.sub);
  const compIdx = sub ? lastIdxOf(sub.comp) : -1;
  const twins = h.occupations.filter((o) => o.code !== occ.code && o.s.every((v, i) => v === occ.s[i]));

  openDrawer(
    esc(occ.title),
    `<p class="drawer-lede">ANZSCO ${esc(occ.code)}${occ.employment !== null ? ` · ${num(Math.round(occ.employment * 1000))} employed` : ''}</p>

     <h3>What this job actually produces</h3>
     <dl class="kv">
       <dt>Serious claims per million hours</dt><dd>${rate(occ.frequency, 1)}</dd>
       <dt>Serious claims per 1,000 employees</dt><dd>${rate(occ.incidence, 1)}</dd>
       <dt>Serious claims a year</dt><dd>${num(occ.claims)}</dd>
     </dl>
     <p class="drawer-note">Five-year averages to 2021–22, published per occupation in the hazards dataset.</p>

     <h3>Highest exposures</h3>
     <div class="bars">${scored
       .slice(0, 10)
       .map(
         (s) => `<div class="bar-row" data-tip="${tipAttr(`${s.name}\n${s.v.toFixed(0)} / 100\n\n${h.descriptions[s.name] ?? ''}`)}">
           <span class="bar-lab">${esc(s.name)}</span>
           <span class="bar-track"><span class="bar-fill" style="width:${s.v.toFixed(1)}%;background:${hazardColor(s.v)}"></span></span>
           <span class="bar-val">${s.v.toFixed(0)}</span>
           <span class="bar-sub">${esc(s.family)}</span>
         </div>`,
       )
       .join('')}</div>

     ${sub
       ? `<h3>The wider occupation group</h3>
          <dl class="kv">
            <dt>Group</dt><dd>${esc(sub.name)}</dd>
            <dt>Serious claims</dt><dd>${num(sub.count[P.length - 1])} <small>${period(P[P.length - 1])}</small></dd>
            <dt>Median compensation</dt><dd>${money(compIdx >= 0 ? sub.comp![compIdx] : null)} <small>${compIdx >= 0 ? period(P[compIdx]) : ''}</small></dd>
            <dt>Median time lost</dt><dd>${weeks(compIdx >= 0 ? sub.lost?.[compIdx] ?? null : null)}</dd>
          </dl>
          <p class="drawer-note">The current annual claims release publishes at the two-digit group level, so these cover every occupation in ${esc(sub.name)}, not just this one.</p>`
       : ''}

     ${twins.length ? `<h3>Identical scores</h3><p class="drawer-note">${twins.length} other occupation${twins.length === 1 ? '' : 's'} carr${twins.length === 1 ? 'ies' : 'y'} exactly the same hazard scores — ${esc(twins.slice(0, 4).map((t) => t.title).join(', '))}${twins.length > 4 ? ', and others' : ''}. This is an artefact of the source, which maps American survey data onto Australian occupation codes.</p>` : ''}

     <p class="drawer-note">Source: Safe Work Australia, Beta Occupational Hazards Dataset (December 2023). CC BY 4.0.</p>`,
  );
}

function lastIdxOf(v: Val[] | undefined): number {
  if (!v) return -1;
  for (let i = v.length - 1; i >= 0; i--) if (v[i] !== null && Number.isFinite(v[i]!)) return i;
  return -1;
}
